import { Deal } from '../types'

/**
 * Margin, the buying fee, and commission — one definition, used everywhere.
 *
 * These rules used to live in two places. `commissionService` worked commission
 * out on the net margin; `salesLedgerService`, which backs the Sales Performance
 * report, worked it out on `gross_margin` and floored losses at zero. Two
 * screens, two answers, both labelled "commission". They sit here now so there
 * is one place to change them and no way for the two to drift apart again.
 */

/**
 * The buying fee is flat, and reduced when the deal loses money — the reduced
 * fee is relief on a bad deal, not a rounding rule. It is decided on the margin
 * BEFORE the fee is charged, so a deal that only goes negative because of the
 * fee still pays the standard one.
 */
export const BUYING_FEE_STANDARD = 500
export const BUYING_FEE_ON_LOSS = 250

/** @deprecated Use buyingFeeFor(). Kept so older imports still compile. */
export const LOAD_FEE = BUYING_FEE_ON_LOSS

export function buyingFeeFor(marginBeforeFee: number | null | undefined): number {
  return Number(marginBeforeFee ?? 0) < 0 ? BUYING_FEE_ON_LOSS : BUYING_FEE_STANDARD
}

/** Sale value: the agreed sale price where there is one, otherwise the estimate. */
export const resaleOf = (d: Deal) =>
  d.sale_price ?? d.estimated_sale_price ?? d.autograb_market_value ?? 0

/**
 * Net margin — §6.1.
 *
 * Commission was once calculated on `gross_margin`, a number written once at deal
 * creation as (Autograb estimate − purchase price). It ignored transport,
 * reconditioning and the buying fee entirely, so every commission figure in the
 * platform was overstated by the cost of getting the car ready and moved.
 *
 * Where the commission has been locked (the tax invoice has been issued) that
 * locked figure wins, because restating a number someone has already been paid
 * against is precisely what the lock exists to prevent.
 *
 * With no sale value at all — not agreed, not estimated, no market value — there
 * is nothing to subtract from, so the stored `gross_margin` is the last resort.
 */
export function netMarginOf(d: Deal): number {
  if (d.commission_locked_net != null) return Number(d.commission_locked_net)

  const sale = Number(resaleOf(d)) || 0
  if (!sale) return Number(d.gross_margin ?? 0)

  const buyIn = Number(d.valuation_amount || 0)
  const capitalised = Number(d.capitalised_transport || 0) + Number(d.capitalised_repairs || 0)
  const outboundTransport =
    d.transport_arranged_by === 'dealer' ? 0 : Number(d.transport_cost || 0)
  const beforeFee = sale - buyIn - capitalised - outboundTransport
  const fee = d.buying_fee != null ? Number(d.buying_fee) : buyingFeeFor(beforeFee)
  return Math.round((beforeFee - fee) * 100) / 100
}

/**
 * GST on commission — Nick, 31 Aug 2026.
 *
 * The share is worked out on the NET margin, and GST goes on at the end: the
 * entitlement is a percentage of the margin exclusive of GST, and ten per cent is
 * added to what the payee invoices. Folding GST into the base would compound it
 * through every downstream percentage and make the payee's tax invoice
 * unpresentable — the share and the tax have to stay separable, because that is
 * how the invoice has to read.
 *
 * A loss is shared the same way (§6.1), so the GST on a negative entitlement is
 * negative too: it reverses exactly as the share does.
 */
export const COMMISSION_GST_RATE = 0.1

const round2 = (n: number) => Math.round(n * 100) / 100

export function commissionOn(netMargin: number, rate: number) {
  const net = round2(netMargin * rate)
  const gst = round2(net * COMMISSION_GST_RATE)
  return { net, gst, payable: round2(net + gst) }
}

/**
 * A person's terms on one deal: a share of the margin, or a flat fee.
 *
 * Winnie, 13 Sep: "Need the option to either enter fix rate or % rate for commission."
 *
 * A fixed fee is deliberately flat. It does not scale with the margin and it is not
 * reduced on a deal that loses money - that is what "fixed" means, and a fixed fee that
 * quietly became a percentage on a bad month would be the kind of surprise that costs
 * more to explain than it saves to pay. The percentage basis is unchanged: it shares the
 * upside and the downside, per §6.1.
 */
export interface CommissionTerms {
  basis: 'percent' | 'fixed'
  /** A fraction. Read only on the percent basis. */
  rate: number
  /** Dollars per deal. Read only on the fixed basis. */
  fixedAmount: number
}

/** A team member row or an attribution row — either carries the terms, under its own names. */
export interface CommissionTermsSource {
  basis?: string | null
  commission_basis?: string | null
  rate?: number | null
  commission_rate?: number | null
  fixed_amount?: number | null
  commission_amount?: number | null
}

/** Terms off a team member row or an attribution row, whichever is to hand. */
export const commissionTerms = (src: CommissionTermsSource | null | undefined): CommissionTerms => ({
  basis: (src?.basis ?? src?.commission_basis) === 'fixed' ? 'fixed' : 'percent',
  rate: Number(src?.rate ?? src?.commission_rate ?? 0) || 0,
  fixedAmount: Number(src?.fixed_amount ?? src?.commission_amount ?? 0) || 0,
})

export function commissionEntitlement(netMargin: number, terms: CommissionTerms) {
  const net = terms.basis === 'fixed' ? round2(terms.fixedAmount) : round2(netMargin * terms.rate)
  const gst = round2(net * COMMISSION_GST_RATE)
  return { net, gst, payable: round2(net + gst) }
}

/** "25%" or "$500 fixed" — one label, so every screen says it the same way. */
export const termsLabel = (terms: CommissionTerms): string =>
  terms.basis === 'fixed'
    ? '$' + terms.fixedAmount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' fixed'
    : Math.round(terms.rate * 1000) / 10 + '%'
